import { useEffect, useRef, useState } from 'react';
import useSelectedLanguage from 'store/language';
import useWebsiteSports, { SportProviders } from 'store/websiteSports';
import useScreen from 'hooks/useScreen';
import useSettings, { IWebsiteConfigs } from 'store/settings';

const TurboStarsWidget = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const { isMobile } = useScreen();
  const turboStarsTopEventsWidgetRef = useRef<HTMLDivElement | null>(null);
  const turboStarsLiveEventsWidgetRef = useRef<HTMLDivElement | null>(null);
  const [sportState] = useWebsiteSports();
  const [{ selectedLanguage }] = useSelectedLanguage();
  const [
    {
      data: { meta = {} as IWebsiteConfigs },
    },
  ] = useSettings();
  const FRAME_HEIGHT = isMobile ? 321 : 339;

  const turboStarsSport = sportState?.data.find((sport) => sport.id === SportProviders.TURBO_STARS);

  useEffect(() => {
    if (!turboStarsSport) return;
    if (isLoaded) return;
    if (!selectedLanguage) return;

    const needLoading = !window.BetSdk;

    const initWidgets = () => {
      const { turboStarsStyles = {} } = meta;

      // Step 1: Define the CSS content
      const cssContent = Object.keys(turboStarsStyles).reduce((acc, key) => {
        return `${acc}${key}: ${turboStarsStyles[key]} !important;`;
      }, '');

      const cssContentUnderRoot = `:root {${cssContent}}`;

      // Step 2: Encode the CSS content to base64
      const base64CSSContent = btoa(cssContentUnderRoot);

      // Step 3: Create the Data URL
      const cssDataURL = `data:text/css;base64,${base64CSSContent}`;

      window.BetSdk.initTopEventsWidget({
        mainFrameUrl: `${window.location.origin}/sport/${SportProviders.TURBO_STARS}`,
        allowParentUrlUpdate: false,
        host: turboStarsSport.url,
        cid: turboStarsSport.cid,
        lang: selectedLanguage?.iso_1,
        containerId: 'top-events-widget-container',
        height: `${FRAME_HEIGHT}px`,
        customStyles: cssDataURL,
      });
      window.BetSdk.initLiveEventsWidget({
        mainFrameUrl: `${window.location.origin}/sport/${SportProviders.TURBO_STARS}`,
        host: turboStarsSport.url,
        cid: turboStarsSport.cid,
        allowParentUrlUpdate: false,
        lang: selectedLanguage?.iso_1,
        containerId: 'live-events-widget-container',
        height: `${FRAME_HEIGHT}px`,
        customStyles: cssDataURL,
      });
    };

    if (needLoading) {
      const script = document.createElement('script');

      script.onload = () => {
        setIsLoaded(true);
        initWidgets();
      };
      script.src = `${turboStarsSport.url}/assets/sdk/init.js`;

      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }

    initWidgets();
  }, [turboStarsSport, selectedLanguage, isLoaded]);

  return (
    <>
      <div id="top-events-widget-container" style={{ height: FRAME_HEIGHT }} ref={turboStarsTopEventsWidgetRef} />
      <div id="live-events-widget-container" style={{ height: FRAME_HEIGHT }} ref={turboStarsLiveEventsWidgetRef} />
    </>
  );
};

export default TurboStarsWidget;
